import { useState, useContext, createContext, ReactNode, useEffect } from "react";
import { translations } from "./translations";
import { supabase } from "@/integrations/supabase/client";

type Lang = "en" | "id";

const STORAGE_KEY = "techmom-language";

interface LanguageContextType {
  lang: Lang;
  setLang: (lang: Lang) => void;
  t: (key: string) => string;
}

const LanguageContext = createContext<LanguageContextType | null>(null);

export const LanguageProvider = ({ children }: { children: ReactNode }) => {
  const [lang, setLangState] = useState<Lang>(() => {
    // Read from localStorage on initial load, default to 'en'
    const saved = localStorage.getItem(STORAGE_KEY);
    return (saved === "en" || saved === "id") ? saved : "en";
  });

  // Sync language preference from database on auth state change
  useEffect(() => {
    const syncLanguageFromProfile = async (userId: string) => {
      const { data } = await supabase
        .from("profiles")
        .select("language_preference")
        .eq("id", userId)
        .single();
      
      if (data?.language_preference && (data.language_preference === "en" || data.language_preference === "id")) {
        setLangState(data.language_preference);
        localStorage.setItem(STORAGE_KEY, data.language_preference);
      }
    };

    // Check initial session
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session?.user) {
        syncLanguageFromProfile(session.user.id);
      }
    });

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === "SIGNED_IN" && session?.user) {
        syncLanguageFromProfile(session.user.id);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  // Persist to localStorage and database when language changes
  const setLang = async (newLang: Lang) => {
    setLangState(newLang);
    localStorage.setItem(STORAGE_KEY, newLang);

    // Sync to database if user is logged in
    const { data: { session } } = await supabase.auth.getSession();
    if (session?.user) {
      await supabase
        .from("profiles")
        .update({ language_preference: newLang })
        .eq("id", session.user.id);
    }
  };

  const t = (key: string) => {
    return translations[lang]?.[key] || key;
  };

  return (
    <LanguageContext.Provider value={{ lang, setLang, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = (): LanguageContextType => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error("useLanguage must be used within LanguageProvider");
  }
  return context;
};

export const useLang = useLanguage;
